'use client';
/** @jsxImportSource @emotion/react */

import React, { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiIconButton, {
  type IconButtonTypeMap as MuiIconButtonTypeMap,
} from '@mui/material/IconButton';

import { useInferButtonSize } from './hooks/useInferButtonSize';

type ExcludedMuiProps = 'disableFocusRipple' | 'disableRipple';

type CustomProps = {
  component?: ElementType;
};

type IconButtonTypeMap<
  P = CustomProps,
  D extends ElementType = MuiIconButtonTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiIconButtonTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `IconButton` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-iconbutton-migration--iconbutton-migration-guide Migration guide}
 */
export type IconButtonProps<
  D extends ElementType = MuiIconButtonTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiIconButtonTypeMap<P, D>, D>;

const IconButtonComponent = (
  { children, ...props }: IconButtonProps,
  ref: Ref<HTMLButtonElement>
) => {
  const inferredSize = useInferButtonSize('size', children);

  return (
    <MuiIconButton
      ref={ref}
      size={inferredSize ? inferredSize : props.size}
      {...props}
    >
      {children}
    </MuiIconButton>
  );
};

IconButtonComponent.displayName = 'IconButton';

/**
 * @deprecated Please use `IconButton` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-iconbutton-migration--iconbutton-migration-guide Migration guide}
 */
export const IconButton = forwardRef(
  IconButtonComponent
) as OverridableComponent<IconButtonTypeMap>;
